import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ResizablePane } from 'styles/common.styles';
import { ActionButtonGroup } from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import api from 'services/api';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { HiGridComponent, useHiGridHook } from 'components/grid/hiGrid';
import { GridExportFocusScope, useRegisterGridExport } from 'components/grid/excel';

const RECORD_JOIN_KEYS = ['employeeId', 'empNo'];

/**
 * 인사기록카드 서브 그리드 (학력·경력·상벌 등)
 */
const EmployeeRecordTabGrid = ({
  tabConfig,
  employeeContext,
  isActive,
  activeChangeSeq,
}) => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const sessionTenant = useTenantCode();
  const lastFetchRef = useRef({ key: '', seq: -1 });
  const { section, selectQuery, defaultRow, columns: columnsFn, id: tabId, label: tabLabel } = tabConfig;
  const employeeId = employeeContext?.employeeId ?? null;
  const tenantCode = employeeContext?.tenantCode ?? null;
  const prefsUserId = useMemo(
    () => buildPrefsUserId(tenantCode || sessionTenant, user),
    [tenantCode, sessionTenant, user]
  );

  const getSearchValues = useCallback(
    () => ({
      employeeId,
      tenantCode,
    }),
    [employeeId, tenantCode]
  );

  const columns = useMemo(() => columnsFn(), [columnsFn]);

  const customOnSave = useCallback(
    async ({ saveData }) => {
      if (!employeeId) {
        throw new Error('사원 기본정보를 먼저 저장하세요.');
      }
      const response = await api.post('/hr/employees/records/save', {
        section,
        employeeId,
        tenantCode,
        rows: saveData,
      });
      const data = response.data || {};
      if (data.Msg !== 'success' && !data.success) {
        throw new Error(data.Msg || '저장 실패');
      }
    },
    [section, employeeId, tenantCode]
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues,
    selectQuery,
    defaultRow,
    autoSearch: false,
    confirmCheck: true,
    customOnSave,
    enableContextMenu: true,
    duplicateExcludeKeys: ['recordId', 'id', 'employeeId'],
  });

  const handleSearchRef = useRef(grid.handleSearch);
  const gridClearRef = useRef(grid.gridClear);
  const handleAddRef = useRef(grid.handleAdd);
  const handleSaveRef = useRef(grid.handleSave);
  const handleDeleteRef = useRef(grid.handleDelete);
  handleSearchRef.current = grid.handleSearch;
  gridClearRef.current = grid.gridClear;
  handleAddRef.current = grid.handleAdd;
  handleSaveRef.current = grid.handleSave;
  handleDeleteRef.current = grid.handleDelete;

  useEffect(() => {
    if (!isActive) return undefined;
    if (!employeeId) {
      gridClearRef.current();
      lastFetchRef.current = { key: '', seq: -1 };
      return undefined;
    }

    const fetchKey = `${section}|${employeeId}`;
    if (
      lastFetchRef.current.key === fetchKey
      && lastFetchRef.current.seq === activeChangeSeq
    ) {
      return undefined;
    }
    lastFetchRef.current = { key: fetchKey, seq: activeChangeSeq };

    let cancelled = false;
    (async () => {
      try {
        await handleSearchRef.current();
      } catch {
        /* handleSearch 내부에서 알림 처리 */
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [isActive, employeeId, activeChangeSeq, section]);

  const onSaveClick = useCallback(() => {
    handleSaveRef.current(
      {},
      () => {
        showAlert('저장되었습니다.', 'success');
        lastFetchRef.current = { key: '', seq: -1 };
        handleSearchRef.current();
      },
      (msg) => showAlert(msg, 'error')
    );
  }, []);

  const onDeleteClick = useCallback(async () => {
    await handleDeleteRef.current();
  }, []);

  const subButtons = useMemo(
    () => [
      {
        id: 'sub-add',
        label: '행추가',
        icon: '➕',
        onClick: () => {
          if (!employeeId) {
            showAlert('사원 기본정보를 먼저 저장한 후 기록을 추가하세요.', 'warning');
            return;
          }
          handleAddRef.current(defaultRow);
        },
        disabled: !employeeId,
      },
      {
        id: 'sub-del',
        label: '행삭제',
        icon: '❌',
        onClick: onDeleteClick,
        variant: 'danger',
        disabled: !employeeId,
      },
      {
        id: 'sub-save',
        label: '저장',
        icon: '✅',
        onClick: onSaveClick,
        variant: 'success',
        disabled: !employeeId,
      },
    ],
    [defaultRow, employeeId, onDeleteClick, onSaveClick]
  );

  const gridExportId = `employee-record-${tabId || section}`;
  const getData = useCallback(() => gridRef.current?.getData?.() ?? [], []);
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);
  const contextValues = useCallback(
    () => ({
      employeeId: employeeContext?.employeeId ?? null,
      empNo: employeeContext?.empNo ?? '',
      personNm: employeeContext?.personNm ?? '',
      tenantCode: employeeContext?.tenantCode ?? '',
    }),
    [employeeContext]
  );

  useRegisterGridExport({
    id: gridExportId,
    label: tabLabel || section,
    getData,
    getDataColumns,
    joinKeys: RECORD_JOIN_KEYS,
    contextValues,
    order: 10,
    enabled: !!employeeId,
  });

  if (!employeeId) {
    return (
      <div style={{ padding: 16, color: '#9CA3AF', flex: 1, fontSize: 13 }}>
        사원을 선택하고 기본정보를 저장하면 {tabConfig.label} 정보를 입력할 수 있습니다.
      </div>
    );
  }

  return (
    <GridExportFocusScope gridId={gridExportId}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, minWidth: 0 }}>
        <ActionButtonGroup buttons={subButtons} loading={grid.loading} showLoadingText={false} />
        <ResizablePane style={{ flex: 1, minHeight: 0 }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            {...grid.contextMenuProps}
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            exportFileName={tabLabel || tabId || '인사기록'}
            gridId={`employee-record-${tabId || section}`}
            prefsUserId={prefsUserId}
          />
        </ResizablePane>
      </div>
    </GridExportFocusScope>
  );
};

export default EmployeeRecordTabGrid;
